import { forwardRef, useImperativeHandle } from "react";
import type { SingleCoverageInformation } from "../../../../data-model/coverageInformation";
import { ManualEvEditClickSource } from "../../../../enums/eventsData";
import { getPlanActivityStatus } from "../../helpers/coverageStatusUtils";
import type { ManualEvFieldEditedDetail } from "../../helpers/evCoverageAnalyticsEvents";
import {
  EvCoverageInsuranceTierTabBar,
  type InsuranceTierSwitcherProps,
} from "../evCoverageInsuranceTierTabBar/evCoverageInsuranceTierTabBar";
import EvCoveragePlanSummaryCard from "../evCoveragePlanSummaryCard/evCoveragePlanSummaryCard";
import EvCoverageSidebar from "../evCoverageSidebar/evCoverageSidebar";
import InactiveInsurance from "../inactiveInsurance/inactiveInsurance";
import EvCoverageActivePlanAndFinancials, {
  type EvCoverageInactiveInsuranceProps,
} from "./evCoverageActivePlanAndFinancials";
import {
  MANUAL_ENTRY_STATUS_CONFIG,
  type ManualEntryStatusConfig,
} from "./evCoverageStatusConfig";
import { EvCoverageStatusLayout } from "./evCoverageStatusLayout";
import { useEvCoverageEditOrchestration } from "./useEvCoverageEditOrchestration";

export type { InsuranceTierSwitcherProps };

export interface EvCoverageUnifiedContentProps {
  singleCoverage: SingleCoverageInformation;
  serviceTypeCodeLabelMap?: ReadonlyMap<string, string>;
  inactiveInsuranceProps: EvCoverageInactiveInsuranceProps;
  insuranceTierSwitcher?: InsuranceTierSwitcherProps;
  orderId: string;
  orgId: string;
  requireBvFields: boolean;
  requireCopayCoinsurance: boolean;
  coverageType: string;
  onCoverageSaved?: () => void;
  hasAttemptedContinue?: boolean;
  continueValidationAttemptId?: number;
  autoEnterEditOnContinueFailure?: boolean;
  focusTreatmentTabIndex?: number;
  onManualEvEditClicked?: (source: ManualEvEditClickSource) => void;
  onManualEvOverrideSaved?: () => void;
  onManualEvFieldEdited?: (detail: ManualEvFieldEditedDetail) => void;
  onEditingChange?: (isEditing: boolean) => void;
  onContinueValidationDismiss?: () => void;
  isManuallyUpdated: boolean;
}

/** Imperative handle for parent Continue validation (includes unsaved local edits). */
export interface EvCoverageUnifiedContentHandle {
  getValidationSnapshot: () => SingleCoverageInformation;
}

const noop = () => {};

export const EvCoverageUnifiedContent = forwardRef<
  EvCoverageUnifiedContentHandle,
  EvCoverageUnifiedContentProps
>((props, ref) => {
  const {
    singleCoverage,
    serviceTypeCodeLabelMap,
    inactiveInsuranceProps,
    insuranceTierSwitcher,
    orderId,
    orgId,
    requireBvFields,
    requireCopayCoinsurance,
    coverageType,
    onCoverageSaved,
    hasAttemptedContinue = false,
    continueValidationAttemptId = 0,
    autoEnterEditOnContinueFailure = false,
    focusTreatmentTabIndex,
    onManualEvEditClicked,
    onManualEvFieldEdited,
    onManualEvOverrideSaved,
    onEditingChange,
    onContinueValidationDismiss,
    isManuallyUpdated,
  } = props;
  const { coverageStatus } = singleCoverage;
  const isActive = getPlanActivityStatus(
    singleCoverage.planInformation.planStatus,
  );

  const {
    displayCoverage,
    isEditing,
    patchPlanInformation,
    applyCoverageEdit,
    treatmentTab,
    setTreatmentTab,
    showManualCoverageEditor,
    showInactiveOverrideEditor,
    showFieldErrors,
    planSummaryEnableEdit,
    editValidation,
    isPersisting,
    handleSaveEdit,
    handleCancelEdit,
    handleBeginEdit,
    openManualCoverageEditor,
  } = useEvCoverageEditOrchestration({
    singleCoverage,
    coverageStatus,
    isActive,
    isManuallyUpdated,
    serviceTypeCodeLabelMap,
    orderId,
    orgId,
    coverageType,
    requireBvFields,
    requireCopayCoinsurance,
    hasAttemptedContinue,
    continueValidationAttemptId,
    autoEnterEditOnContinueFailure,
    focusTreatmentTabIndex,
    onCoverageSaved,
    onManualEvEditClicked,
    onManualEvOverrideSaved,
    onEditingChange,
    onContinueValidationDismiss,
  });

  useImperativeHandle(
    ref,
    () => ({
      getValidationSnapshot: () => displayCoverage,
    }),
    [displayCoverage],
  );

  /**
   * Editable plan summary bound to local coverage edit state (same card as the successful EV path).
   *
   * @returns Plan summary card for the current coverage row
   */
  const renderEditablePlanSummaryCard = () => (
    <EvCoveragePlanSummaryCard
      singleCoverage={displayCoverage}
      planStatusVariant={isActive}
      enablePlanSummaryEdit={planSummaryEnableEdit}
      isEditing={isEditing}
      isSaving={isPersisting}
      onBeginEdit={handleBeginEdit}
      onCancelEdit={handleCancelEdit}
      onSaveEdit={handleSaveEdit}
      onPlanInformationChange={patchPlanInformation}
      onManualEvFieldEdited={onManualEvFieldEdited}
      showFieldErrors={showFieldErrors}
      invalidFieldIds={editValidation.invalidFieldIds}
    />
  );

  const renderActivePlanAndFinancials = () => (
    <EvCoverageActivePlanAndFinancials
      planSummary={renderEditablePlanSummaryCard()}
      displayCoverage={displayCoverage}
      isEditing={isEditing}
      isActive={isActive}
      serviceTypeCodeLabelMap={serviceTypeCodeLabelMap}
      treatmentTab={treatmentTab}
      onTreatmentTabChange={setTreatmentTab}
      onApplyCoverageEdit={applyCoverageEdit}
      onManualEvFieldEdited={onManualEvFieldEdited}
      showFieldErrors={showFieldErrors}
      invalidFieldIds={editValidation.invalidFieldIds}
      inactiveInsuranceProps={inactiveInsuranceProps}
    />
  );

  const renderManualEntryStatus = (config: ManualEntryStatusConfig) => {
    const isEditorOpen = config.showUpdateManually
      ? showManualCoverageEditor
      : isEditing;
    if (isEditorOpen) {
      return renderActivePlanAndFinancials();
    }
    return (
      <EvCoverageStatusLayout planSummary={renderEditablePlanSummaryCard()}>
        <InactiveInsurance
          variant={config.variant}
          onCommentsChange={noop}
          onUpdateManually={
            config.showUpdateManually ? openManualCoverageEditor : undefined
          }
        />
      </EvCoverageStatusLayout>
    );
  };

  const renderInactivePlanFallback = () => {
    if (showInactiveOverrideEditor) {
      return renderActivePlanAndFinancials();
    }
    return (
      <EvCoverageStatusLayout
        spacing="flush"
        planSummary={
          <EvCoveragePlanSummaryCard
            singleCoverage={singleCoverage}
            planStatusVariant="inactive"
            enablePlanSummaryEdit
            onBeginEdit={handleBeginEdit}
          />
        }
      >
        <InactiveInsurance {...inactiveInsuranceProps} />
      </EvCoverageStatusLayout>
    );
  };

  const renderMain = () => {
    const manualEntryConfig = coverageStatus
      ? MANUAL_ENTRY_STATUS_CONFIG[coverageStatus]
      : undefined;
    // Medicaid skips only the Unable-to-Retrieve / not-supported "Update Manually"
    // shells (manual BV financial entry). IN_PROGRESS must still show the fetching
    // panel so an in-flight EV is not mistaken for missing Medicaid details.
    const skipManualEntryShell =
      !!displayCoverage.medicaidInformation &&
      !!manualEntryConfig?.showUpdateManually;

    if (manualEntryConfig && !isManuallyUpdated && !skipManualEntryShell) {
      return renderManualEntryStatus(manualEntryConfig);
    }

    if (isActive === "inactive") {
      return renderInactivePlanFallback();
    }

    return renderActivePlanAndFinancials();
  };

  return (
    <div className="ev-coverage-unified grid h-full min-h-0 w-full grid-cols-[3fr_7fr] gap-2 pt-1 sm:gap-3 md:gap-4 md:pt-2 lg:gap-5 lg:pt-3">
      <EvCoverageSidebar singleCoverage={singleCoverage} />
      <div className="ev-coverage-unified__main flex min-h-0 min-w-0 flex-col overflow-hidden">
        {insuranceTierSwitcher && (
          <EvCoverageInsuranceTierTabBar {...insuranceTierSwitcher} />
        )}
        {renderMain()}
      </div>
    </div>
  );
});

EvCoverageUnifiedContent.displayName = "EvCoverageUnifiedContent";

export default EvCoverageUnifiedContent;
